import { useState } from "react";
import { Drawer } from "@/components/ui/Drawer";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Toolbar, SegmentedFilter } from "@/components/ui/Toolbar";
import { Icon } from "@/components/ui/Icon";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { useAction, useList } from "@/lib/api/queries";
import { useSession } from "@/lib/auth/useSession";
import { PERM } from "@/lib/auth/rbac";
import { dateTime, relative, text } from "@/lib/format";
import { roleLabel } from "@/lib/labels";

interface User {
  id: string;
  username: string;
  full_name: string;
  is_active: boolean;
  roles: string[];
  permissions: string[];
  warehouses: string[];
  version: number;
  created_at: string;
  updated_at: string;
}

interface Role {
  id: string;
  code: string;
  name: string;
  description: string;
  is_system: boolean;
  permissions: string[];
}

interface Device {
  id: string;
  device_code: string;
  model?: string;
  status: string;
  assigned_user_id?: string;
  last_seen_at?: string;
  created_at: string;
}

interface Warehouse {
  id: string;
  code: string;
  name?: string;
}

type Tab = "users" | "roles" | "devices";

export function Security() {
  const [tab, setTab] = useState<Tab>("users");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<User | Role | null>(null);
  // Xodim formasi: "new" — yangi, User — tahrirlash. УНФ kengaytmasi
  // xodimlarni yubormaydi, shuning uchun ular shu yerda yaratiladi.
  const [form, setForm] = useState<User | "new" | null>(null);
  const session = useSession();
  const canWrite = session.can(PERM.userWrite);

  // Qidiruv SERVERDA (`q`): xodimlar yuzlab, ro'yxat 200 bilan cheklangan —
  // mijozdagi filtr faqat birinchi 200 ichidan qidirib, qolganini topmasdi.
  const users = useList<User>("identity", "/api/v1/users", { limit: 200, q: search.trim() || undefined },
    { enabled: tab === "users" });
  const roles = useList<Role>("identity", "/api/v1/roles", { limit: 100 }, { enabled: tab === "roles" });
  const devices = useList<Device>("identity", "/api/v1/devices", { limit: 200 }, { enabled: tab === "devices" });

  const active = tab === "users" ? users : tab === "roles" ? roles : devices;
  const needle = search.trim().toLowerCase();

  const userRows = (users.data?.items ?? []).filter((row) =>
    needle
      ? [row.username, row.full_name, ...row.roles, ...row.roles.map(roleLabel)].some((v) =>
          v?.toLowerCase().includes(needle),
        )
      : true,
  );
  const roleRows = (roles.data?.items ?? []).filter((row) =>
    needle ? [row.code, row.name].some((v) => v?.toLowerCase().includes(needle)) : true,
  );
  const deviceRows = (devices.data?.items ?? []).filter((row) =>
    needle ? [row.device_code, row.model].some((v) => v?.toLowerCase().includes(needle)) : true,
  );

  const userColumns: Array<Column<User>> = [
    { key: "username", header: "Login", render: (row) => <span className="mono">{row.username}</span> },
    { key: "name", header: "Ism", render: (row) => text(row.full_name) },
    {
      key: "roles",
      header: "Rollar",
      render: (row) => (
        <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {row.roles.length > 0
            ? row.roles.map((role) => (
                <StatusBadge key={role} value={role} label={roleLabel(role)} tone="tag" />
              ))
            : "—"}
        </span>
      ),
    },
    {
      key: "permissions",
      header: "Vakolat",
      align: "right",
      // Vakolatlar ROLDAN keladi: son ko'rsatiladi, ro'yxat esa
      // tafsilot panelida.
      render: (row) => <span className="num">{row.permissions.length}</span>,
    },
    {
      key: "status",
      header: "Holat",
      render: (row) => (
        <StatusBadge value={row.is_active ? "faol" : "nofaol"} tone={row.is_active ? "success" : "neutral"} />
      ),
    },
    {
      key: "updated",
      header: "Yangilandi",
      render: (row) => <span title={dateTime(row.updated_at)}>{relative(row.updated_at)}</span>,
    },
    {
      key: "open",
      header: "",
      align: "right",
      render: (row) => (
        <button type="button" className="btn btn-icon" onClick={() => setSelected(row)} title="Vakolatlar">
          <Icon name="chevronRight" size={16} />
        </button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Foydalanuvchi va rollar"
        hint="Vakolatlar rol orqali beriladi. UI va API bir xil server siyosatiga tayanadi: interfeysda tugmani yashirish yetarli emas, har bir amal serverda tekshiriladi."
      />

      <Toolbar
        search={search}
        onSearch={setSearch}
        placeholder="Login, ism yoki rol bo'yicha…"
        onRefresh={() => void active.refetch()}
        refreshing={active.isFetching}
      >
        <SegmentedFilter
          value={tab}
          onChange={setTab}
          options={[
            { value: "users", label: "Xodimlar" },
            { value: "roles", label: "Rollar" },
            { value: "devices", label: "Qurilmalar" },
          ]}
        />
        {tab === "users" && canWrite && (
          <button type="button" className="btn btn-filled" onClick={() => setForm("new")}>
            <Icon name="check" size={16} />
            Yangi xodim
          </button>
        )}
      </Toolbar>

      {tab === "users" && (
        <DataTable
          columns={userColumns}
          rows={userRows}
          rowKey={(row) => row.id}
          isLoading={users.isLoading}
          error={users.error}
          onRetry={() => void users.refetch()}
          emptyTitle="Xodim topilmadi"
          hasMore={users.data?.has_more}
          footer={<span>{userRows.length} ta xodim</span>}
        />
      )}

      {tab === "roles" && (
        <DataTable
          columns={[
            { key: "code", header: "Kod", render: (row: Role) => <span className="mono">{row.code}</span> },
            { key: "name", header: "Nomi", render: (row: Role) => row.name },
            { key: "desc", header: "Tavsif", render: (row: Role) => text(row.description) },
            {
              key: "system",
              header: "Manba",
              // Tizim roli KODDAN sinxronlanadi: uni tahrirlash
              // keyingi deployda bekor qilinardi.
              render: (row: Role) => (
                <StatusBadge value={row.is_system ? "koddan" : "qo'lda"} tone="tag" />
              ),
            },
            {
              key: "perms",
              header: "Vakolat",
              align: "right",
              render: (row: Role) => <span className="num">{row.permissions.length}</span>,
            },
            {
              key: "open",
              header: "",
              align: "right",
              render: (row: Role) => (
                <button type="button" className="btn btn-icon" onClick={() => setSelected(row)}>
                  <Icon name="chevronRight" size={16} />
                </button>
              ),
            },
          ]}
          rows={roleRows}
          rowKey={(row) => row.id}
          isLoading={roles.isLoading}
          error={roles.error}
          onRetry={() => void roles.refetch()}
          emptyTitle="Rol topilmadi"
          footer={<span>{roleRows.length} ta rol</span>}
        />
      )}

      {tab === "devices" && (
        <DataTable
          columns={[
            { key: "code", header: "Qurilma", render: (row: Device) => <span className="mono">{row.device_code}</span> },
            { key: "model", header: "Model", render: (row: Device) => text(row.model) },
            { key: "status", header: "Holat", render: (row: Device) => <StatusBadge value={row.status} /> },
            {
              key: "seen",
              header: "Oxirgi faollik",
              render: (row: Device) =>
                row.last_seen_at ? (
                  <span title={dateTime(row.last_seen_at)}>{relative(row.last_seen_at)}</span>
                ) : (
                  <span style={{ color: "var(--outline)" }}>—</span>
                ),
            },
          ]}
          rows={deviceRows}
          rowKey={(row) => row.id}
          isLoading={devices.isLoading}
          error={devices.error}
          onRetry={() => void devices.refetch()}
          emptyTitle="Qurilma ro'yxatdan o'tmagan"
          emptyHint="TSD qurilmalari ro'yxatdan o'tgach shu yerda ko'rinadi."
          footer={<span>{deviceRows.length} ta qurilma</span>}
        />
      )}

      {selected && (
        <PermissionDetail
          subject={selected}
          onClose={() => setSelected(null)}
          onEdit={canWrite && "username" in selected ? () => setForm(selected) : undefined}
        />
      )}
      {form && (
        <UserForm
          user={form === "new" ? null : form}
          onClose={() => setForm(null)}
          onSaved={() => {
            setForm(null);
            setSelected(null);
          }}
        />
      )}
    </>
  );
}

/** Xodim yoki rolning vakolatlari. */
function PermissionDetail({
  subject,
  onClose,
  onEdit,
}: {
  subject: User | Role;
  onClose: () => void;
  onEdit?: () => void;
}) {
  const isUser = "username" in subject;
  const permissions = [...subject.permissions].sort();

  // Vakolatlar domen bo'yicha guruhlanadi: 57 ta tekis ro'yxatni
  // o'qib bo'lmaydi.
  const byDomain = new Map<string, string[]>();
  for (const permission of permissions) {
    const domain = permission.split(".")[0] ?? "boshqa";
    byDomain.set(domain, [...(byDomain.get(domain) ?? []), permission]);
  }

  return (
    <Drawer label="Vakolatlar">
      <div className="drawer-head">
        <div>
          <div className="dialog-title">
            {isUser ? subject.full_name || subject.username : subject.name}
          </div>
          <div className="dialog-desc mono">
            {isUser ? subject.username : subject.code}
          </div>
        </div>
        <button type="button" className="btn btn-icon" onClick={onClose} aria-label="Yopish">
          <Icon name="close" />
        </button>
      </div>

      <div className="drawer-body">
        {isUser && (
          <dl className="kv">
            <dt>Rollar</dt>
            <dd>{subject.roles.map(roleLabel).join(", ") || "—"}</dd>
            <dt>Omborlar</dt>
            <dd>{subject.warehouses.join(", ") || "barchasi"}</dd>
            <dt>Holat</dt>
            <dd>{subject.is_active ? "faol" : "nofaol"}</dd>
          </dl>
        )}
        {onEdit && (
          <button type="button" className="btn btn-tonal" onClick={onEdit} style={{ justifySelf: "start" }}>
            <Icon name="settings" size={16} />
            O'zgartirish (rol, ombor, parol, holat)
          </button>
        )}

        <div className="section">
          <div className="section-title">
            Vakolatlar ({permissions.length})
          </div>
          {[...byDomain.entries()].map(([domain, items]) => (
            <div key={domain} className="perm-group">
              <div className="perm-domain">{domain}</div>
              <div className="perm-list">
                {items.map((permission) => (
                  <span key={permission} className="badge">
                    {permission.slice(domain.length + 1)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Drawer>
  );
}

/**
 * Xodim yaratish va o'zgartirish.
 *
 * Parol faqat yaratishda majburiy; tahrirlashda bo'sh qolsa o'zgarmaydi.
 * Parol siyosatini server tekshiradi (xato dialogda ko'rinadi). Ishdan
 * ketgan xodim o'chirilmaydi — nofaol qilinadi: audit izi saqlanadi va
 * serverda uning sessiyalari yopiladi.
 */
function UserForm({ user, onClose, onSaved }: { user: User | null; onClose: () => void; onSaved: () => void }) {
  const [username, setUsername] = useState(user?.username ?? "");
  const [fullName, setFullName] = useState(user?.full_name ?? "");
  const [password, setPassword] = useState("");
  const [roles, setRoles] = useState<string[]>(user?.roles ?? []);
  const [warehouses, setWarehouses] = useState<string[]>(user?.warehouses ?? []);
  const [active, setActive] = useState(user?.is_active ?? true);

  const roleList = useList<Role>("identity", "/api/v1/roles", { limit: 100 });
  const warehouseList = useList<Warehouse>("topology", "/api/v1/warehouses", { limit: 50 });

  const save = useAction<User, void>("identity", () =>
    user
      ? {
          path: `/api/v1/users/${user.id}`,
          method: "PATCH",
          body: {
            full_name: fullName.trim(),
            roles,
            warehouses,
            is_active: active,
            version: user.version,
            ...(password ? { password } : {}),
          },
        }
      : {
          path: "/api/v1/users",
          method: "POST",
          body: { username: username.trim(), full_name: fullName.trim(), password, roles, warehouses, is_active: active },
        },
  );

  const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
  const invalid = fullName.trim() === "" || roles.length === 0 || (!user && (username.trim() === "" || password === ""));

  return (
    <ConfirmDialog
      open
      wide
      title={user ? `Xodim: ${user.username}` : "Yangi xodim"}
      description="Rol TSD da qaysi ish berilishini belgilaydi (qabul, kara, terish, nazorat, jo'natish, sanash). Ombor bo'sh — barcha omborlar."
      confirmLabel="Saqlash"
      busy={save.isPending}
      error={save.error}
      confirmDisabled={invalid}
      onClose={onClose}
      onConfirm={() => save.mutate(undefined, { onSuccess: onSaved })}
      extra={
        <div style={{ display: "grid", gap: 12 }}>
          <div className="grid-2">
            <label className="field">
              <span className="field-label">Login</span>
              <input
                className="input mono"
                value={username}
                disabled={user !== null}
                autoComplete="off"
                onChange={(event) => setUsername(event.target.value)}
              />
            </label>
            <label className="field">
              <span className="field-label">F.I.Sh.</span>
              <input className="input" value={fullName} onChange={(event) => setFullName(event.target.value)} />
            </label>
          </div>
          <label className="field">
            <span className="field-label">{user ? "Yangi parol (bo'sh — o'zgarmaydi)" : "Parol"}</span>
            <input
              className="input"
              type="password"
              value={password}
              autoComplete="new-password"
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          <div className="field">
            <span className="field-label">Rollar</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px" }}>
              {(roleList.data?.items ?? []).map((role) => (
                <label key={role.code} style={{ display: "inline-flex", gap: 6, alignItems: "center" }} title={role.description}>
                  <input
                    type="checkbox"
                    checked={roles.includes(role.code)}
                    onChange={() => setRoles(toggle(roles, role.code))}
                  />
                  {role.name} <span className="mono" style={{ color: "var(--on-surface-variant)" }}>{role.code}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="field">
            <span className="field-label">Omborlar</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px" }}>
              {(warehouseList.data?.items ?? []).map((warehouse) => (
                <label key={warehouse.code} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={warehouses.includes(warehouse.code)}
                    onChange={() => setWarehouses(toggle(warehouses, warehouse.code))}
                  />
                  {warehouse.code}
                  {warehouse.name ? ` — ${warehouse.name}` : ""}
                </label>
              ))}
            </div>
          </div>
          <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} />
            Faol (nofaol xodim tizimga kira olmaydi, sessiyalari yopiladi)
          </label>
        </div>
      }
    />
  );
}
